import { useEffect, useState } from 'react'
import { TraceRegion } from '../lib/trace'
import { ApiError, api, type Interest } from '../api'
import { radius, theme, TAP_MIN } from '../theme'
import { Button, Card, Heading, Page, inputStyle } from '../components/ui'
import { ErrorScreen, Loading } from '../components/States'

/**
 * "What are you into this week?"
 *
 * Fires on the very first session, then once a month. Decided 18 Sep 2026.
 *
 * What it is for: the jokes, the comic captions and the example sentences get
 * written around whatever a child actually cares about. A word inside a
 * Batman sentence is a different word from one inside a sentence about a
 * bank clerk.
 */
export function InterestCheckIn({
  learnerId, displayName, onDone,
}: { learnerId: string; displayName: string; onDone: () => void }) {
  const [all, setAll] = useState<Interest[] | null>(null)
  const [chosen, setChosen] = useState<Set<string>>(new Set())
  const [typed, setTyped] = useState('')
  const [added, setAdded] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [refused, setRefused] = useState<string | null>(null)

  const load = () => {
    setError(null)
    api.interests(learnerId)
      .then((r) => { setAll(r.all); setChosen(new Set(r.chosen)) })
      .catch(setError)
  }
  useEffect(load, [learnerId])

  if (error) return <ErrorScreen error={error} onRetry={load} />
  if (!all) return <Loading />

  const toggle = (id: string) => {
    setChosen((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const addTyped = () => {
    const label = typed.trim()
    if (!label || added.includes(label)) return
    setRefused(null)
    setAdded((a) => [...a, label])
    setTyped('')
  }

  const save = async (skipped: boolean) => {
    setBusy(true)
    try {
      await api.setInterests(learnerId, {
        interest_ids: [...chosen],
        new_labels: added,
        skipped,
      })
      onDone()
    } catch (err) {
      // A topic the app will not write stories about. The child sees the
      // reason and the chip comes back off, rather than a blank failure.
      if (err instanceof ApiError && err.status === 400) {
        setRefused(err.detail)
        setAdded((a) => a.filter((label) =>
          !err.detail.toLowerCase().includes(label.toLowerCase())))
      } else {
        setError(err)
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <Page width={560}>
      <TraceRegion id="T17" writes note="your picks are saved as rows of learner_interest; anything new you type is added to T16 interest first">
      <Heading sub="Today's jokes, stories and examples will use it.">
        What are you into, {displayName}?
      </Heading>

      <Card>
        <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
          {all.map((interest) => {
            const on = chosen.has(interest.interest_id)
            return (
              <button
                key={interest.interest_id}
                type="button"
                onClick={() => toggle(interest.interest_id)}
                style={{
                  fontFamily: 'inherit', fontSize: 16, fontWeight: on ? 700 : 600,
                  color: on ? theme.orangeDark : theme.text2,
                  background: on ? theme.orangePale : '#FFFFFF',
                  border: `2px solid ${on ? theme.orange : theme.border}`,
                  borderRadius: radius.pill, padding: '10px 16px',
                  minHeight: TAP_MIN, cursor: 'pointer',
                }}
              >
                {interest.emoji ? `${interest.emoji} ` : ''}{interest.label}
              </button>
            )
          })}
          {added.map((label) => (
            <span
              key={label}
              style={{
                fontSize: 16, fontWeight: 700, color: theme.greenDarker,
                background: theme.greenPale, border: `2px solid ${theme.green}`,
                borderRadius: radius.pill, padding: '10px 16px',
                minHeight: TAP_MIN, display: 'inline-flex', alignItems: 'center',
              }}
            >
              {label}
            </span>
          ))}
        </div>

        {refused ? (
          <p style={{
            margin: '16px 0 0', padding: '10px 12px', background: theme.orangePale,
            borderRadius: 8, fontSize: 15, color: theme.orangeDark, lineHeight: 1.5,
          }}>
            {refused}
          </p>
        ) : null}

        <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
          <input
            style={{ ...inputStyle, flex: 1 }}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTyped() } }}
            placeholder="Something else you like"
          />
          <Button tone="quiet" onClick={addTyped} disabled={!typed.trim()}>Add</Button>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 22, flexWrap: 'wrap' }}>
          <Button tone="child" disabled={busy} onClick={() => save(false)} style={{ flex: 1 }}>
            That&apos;s me
          </Button>
          {/* "Not now" still counts as having asked, so it does not come back
              tomorrow. Being nagged is worse than being asked once. */}
          <Button tone="ghost" disabled={busy} onClick={() => save(true)}>
            Not now
          </Button>
        </div>
      </Card>

      <p style={{ marginTop: 14, color: theme.faint, fontSize: 13, textAlign: 'center' }}>
        We will ask again in about a month. You can change it any time from the
        session map.
      </p>
      </TraceRegion>
    </Page>
  )
}
